import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';

const Navigation = () => {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: t('nav.experience'), href: '#experience' },
    { label: t('nav.house'), href: '#house' },
    { label: t('nav.studio'), href: '#studio' },
    { label: t('nav.artists'), href: '#artists' },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (!isHome) {
      window.location.href = '/' + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#F5F1EC] ${
        scrolled ? 'shadow-[0_1px_0_#D9D3CC]' : ''
      }`}
    >
      <div className="px-5 md:px-10 lg:px-14 flex items-center justify-between h-14 md:h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/logo-rh.png" alt="RHR" className="h-7 w-7 object-cover" />
          <span className="hidden sm:inline font-heading font-bold text-[15px] tracking-tight text-[#E92A3E]">
            Red House Residency
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => scrollTo(link.href)}
              className="text-[13px] text-[#555] hover:text-[#111] transition-colors"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={toggle}
            data-testid="language-toggle"
            className="text-[10px] font-mono tracking-wider px-2 py-1 rounded border border-[#D9D3CC] text-[#888] hover:text-[#111] hover:border-[#111] transition-all"
          >
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>

          <div className="hidden md:block">
            <Link to="/inquiry" data-testid="nav-cta" className="btn-primary rounded">
              {t('nav.booking')}
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-[#111]"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#F5F1EC] border-t border-[#D9D3CC] overflow-hidden"
          >
            <div className="px-5 py-5 space-y-3">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollTo(link.href)}
                  className="block w-full text-left text-[15px] text-[#111] py-2"
                >
                  {link.label}
                </button>
              ))}
              <Link
                to="/inquiry"
                onClick={() => setMobileOpen(false)}
                className="block w-full btn-primary rounded text-center mt-3"
              >
                {t('nav.booking')}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
